/**
 * Reusable structured address form component.
 *
 * Renders address_line_1, address_line_2, city, lga (cascading from state),
 * state (dropdown for Nigeria), postal_code, and country (flag dropdown).
 *
 * Props:
 *  - values: current address field values
 *  - onChange: called with (fieldName, value) on every change
 *  - prefix: optional key prefix for emergency contact addresses etc.
 *  - defaultCountryCode: ISO 2-letter code to default country (from company settings)
 *  - disabled: disable all fields
 *  - compact: use size="small" on all fields
 *  - readOnly: display mode (no editing)
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { TYPE } from '@/theme/typography';
import CountrySelect from '@/components/common/CountrySelect';
import { NIGERIAN_STATES, getLGAsForState, findStateId } from '@/data/nigerianStates';
import { getStatesForCountry } from '@/data/countries';

export interface AddressFields {
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  lga?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

interface AddressFormProps {
  values: AddressFields;
  onChange: (field: string, value: string) => void;
  defaultCountryCode?: string;
  disabled?: boolean;
  compact?: boolean;
  size?: 'small' | 'medium';
  /** Hide the country field (when country is already selected elsewhere) */
  hideCountry?: boolean;
}

export default function AddressForm({
  values,
  onChange,
  defaultCountryCode = 'NG',
  disabled = false,
  compact = false,
  size: sizeProp,
  hideCountry = false,
}: AddressFormProps) {
  const size = sizeProp || (compact ? 'small' : 'medium');
  const country = values.country || defaultCountryCode || '';
  const isNigeria = country === 'NG';

  // Resolve state id for LGA cascading
  const stateId = useMemo(() => {
    if (!isNigeria || !values.state) return null;
    return findStateId(values.state);
  }, [isNigeria, values.state]);

  const lgaOptions = useMemo(() => {
    if (!stateId) return [];
    return getLGAsForState(stateId).map((l) => l.name);
  }, [stateId]);

  // State options — Nigeria from full dataset, others from countries.ts
  const stateOptions = useMemo(() => {
    if (isNigeria) return NIGERIAN_STATES.map((s) => s.name);
    return getStatesForCountry(country);
  }, [isNigeria, country]);

  const hasStates = stateOptions.length > 0;

  // When country changes, clear state and LGA (new country = different state list)
  const [prevCountry, setPrevCountry] = useState(country);
  useEffect(() => {
    if (country !== prevCountry) {
      setPrevCountry(country);
      onChange('state', '');
      onChange('lga', '');
    }
  }, [country, prevCountry, onChange]);

  // When state changes, clear LGA if it's no longer valid
  useEffect(() => {
    if (values.lga && lgaOptions.length > 0 && !lgaOptions.includes(values.lga)) {
      onChange('lga', '');
    }
  }, [values.state]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid container spacing={2}>
      {/* Country — hidden when parent already provides a country selector */}
      {!hideCountry && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <CountrySelect
            value={country}
            onChange={(code) => onChange('country', code)}
            size={size}
            disabled={disabled}
          />
        </Grid>
      )}

      {/* State / Province */}
      <Grid size={{ xs: 12, sm: 6 }}>
        {hasStates ? (
          <Autocomplete
            size={size}
            options={stateOptions}
            value={stateOptions.includes(values.state || '') ? values.state : null}
            onChange={(_, v) => onChange('state', v || '')}
            disabled={disabled}
            freeSolo
            onInputChange={(_, v, reason) => { if (reason === 'input') onChange('state', v); }}
            renderInput={(params) => (
              <TextField {...params} label={isNigeria ? 'State' : 'State / Province'} />
            )}
          />
        ) : (
          <TextField
            fullWidth
            size={size}
            label="State / Province / Region"
            value={values.state || ''}
            onChange={(e) => onChange('state', e.target.value)}
            disabled={disabled}
          />
        )}
      </Grid>

      {/* LGA — only for Nigeria */}
      {isNigeria && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            size={size}
            options={lgaOptions}
            value={values.lga || null}
            onChange={(_, v) => onChange('lga', v || '')}
            disabled={disabled || !values.state}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Local Government Area"
                placeholder={values.state ? 'Select LGA' : 'Select state first'}
              />
            )}
          />
        </Grid>
      )}

      {/* City / Town */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          size={size}
          label="City / Town"
          value={values.city || ''}
          onChange={(e) => onChange('city', e.target.value)}
          disabled={disabled}
        />
      </Grid>

      {/* Address Line 1 */}
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          size={size}
          label="Street Address"
          placeholder="Street number and name"
          value={values.address_line_1 || ''}
          onChange={(e) => onChange('address_line_1', e.target.value)}
          disabled={disabled}
        />
      </Grid>

      {/* Address Line 2 */}
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          size={size}
          label="Address Line 2"
          placeholder="Apartment, suite, landmark (optional)"
          value={values.address_line_2 || ''}
          onChange={(e) => onChange('address_line_2', e.target.value)}
          disabled={disabled}
        />
      </Grid>

      {/* Postal Code */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          size={size}
          label="Postal Code"
          placeholder={isNigeria ? 'Optional' : ''}
          value={values.postal_code || ''}
          onChange={(e) => onChange('postal_code', e.target.value)}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
}

/** Helper: extract address fields from a flat form object (with optional prefix) */
// eslint-disable-next-line react-refresh/only-export-components
export function extractAddressFields(obj: Record<string, any>, prefix = ''): AddressFields {
  const p = prefix;
  return {
    address_line_1: obj[`${p}address_line_1`] || '',
    address_line_2: obj[`${p}address_line_2`] || '',
    city: obj[`${p}city`] || '',
    lga: obj[`${p}lga`] || '',
    state: obj[`${p}state`] || '',
    postal_code: obj[`${p}postal_code`] || '',
    country: obj[`${p}country`] || '',
  };
}

/** Helper: spread address fields back into a flat object (with optional prefix) */
// eslint-disable-next-line react-refresh/only-export-components
export function flattenAddressFields(fields: AddressFields, prefix = ''): Record<string, string> {
  const p = prefix;
  return {
    [`${p}address_line_1`]: fields.address_line_1 || '',
    [`${p}address_line_2`]: fields.address_line_2 || '',
    [`${p}city`]: fields.city || '',
    [`${p}lga`]: fields.lga || '',
    [`${p}state`]: fields.state || '',
    [`${p}postal_code`]: fields.postal_code || '',
    [`${p}country`]: fields.country || '',
  };
}

/** Display-only address component for detail pages */
export function AddressDisplay({ values, label: _label }: { values: AddressFields; label?: string }) {
  const parts = [
    values.address_line_1,
    values.address_line_2,
    [values.city, values.lga].filter(Boolean).join(', '),
    values.state,
    values.postal_code,
  ].filter(Boolean);

  if (!parts.length) return null;

  return (
    <>
      {parts.map((line, i) => (
        <div key={i} style={{ fontSize: TYPE.base, color: i === 0 ? 'inherit' : undefined }}>
          {line}
        </div>
      ))}
    </>
  );
}
